import { createContext, useCallback, useContext, useState } from 'react';
import { TemplateEditViews } from 'modules/templateEditor/common';
import noop from 'utils/noop';

export const viewOptions = Object.entries(TemplateEditViews).reduce(
  (acc: { [key: string]: TemplateEditViews }, [key, value]) => ({ ...acc, [key]: value }),
  {},
);

interface TemplateEditorState {
  isOpen?: boolean;
  view?: TemplateEditViews | string;
}

export interface TemplateTakeoverContextState extends TemplateEditorState {
  viewOptions: { [key: string]: TemplateEditViews };
  setView: (view?: TemplateEditViews | string) => void;
  set: (options: TemplateEditorState) => void;
  open: (view?: TemplateEditViews) => void;
  close: () => void;
}

const defaults = {
  isOpen: false,
  view: TemplateEditViews.preview,
  viewOptions,
};

export const TemplateTakeoverContext = createContext<TemplateTakeoverContextState>({
  ...defaults,
  setView: noop,
  set: noop,
  open: noop,
  close: noop,
  viewOptions,
});

const isTemplateEditView = (view: any): view is TemplateEditViews =>
  typeof view === 'string' && !!viewOptions[view];

export const TemplateTakeoverContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState<TemplateEditorState['isOpen']>(defaults.isOpen);
  const [view, setView] = useState<TemplateEditorState['view']>(defaults.view);

  const set = useCallback(
    (options: TemplateEditorState) => {
      if (isTemplateEditView(options.view) && options.view !== view) {
        setView(options.view);
      }
      if (typeof options.isOpen === 'boolean' && options.isOpen !== isOpen) {
        setIsOpen(options.isOpen);
      }
    },
    [setIsOpen, setView, isOpen, view],
  );

  const open = useCallback(
    (openView = TemplateEditViews.select) => set({ isOpen: true, view: openView }),
    [set],
  );

  const close = useCallback(() => set({ ...defaults, isOpen: false }), [set]);

  return (
    <TemplateTakeoverContext.Provider
      value={{
        isOpen,
        view,
        viewOptions,
        setView,
        set,
        open,
        close,
      }}
    >
      {children}
    </TemplateTakeoverContext.Provider>
  );
};

export const useTemplateTakeover = () => useContext(TemplateTakeoverContext);
